import React, { Suspense, useEffect, useReducer, useRef, useState } from "react";
import { connect } from "react-redux";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { Button, Loader, TextInput } from "../../_components";
import TextButton from "../../_components/atoms/text-button";
import { InputSwitch } from "../../_components/form-controls/input-switch.component";
import IconButton2 from "../../_components/icon-button-2-component";
import CopyTextField from "../../_components/molecule/copy-text-field.component";
import UIcon from "../../_components/uicon-component";
import UploadProgressBar from "../../_components/upload-progress-bar.component";
import { socketEndpointBase } from "../../_config/endpoint.config/endpoint.config";
import { EmailService } from "../../_service";
const RichTextEditor = React.lazy(() => import("../../_components/form-controls/rich-text-editor.component"));

export const INVITE_MEMBERS_SETTINGS_PAGE_ROUTE = "/settings/invite";

/**
 * @param {Object} user - Auth user object
 * @param {Object} community - Current community object
 * @param {Array<Object>} groups - List of groups
 * @param {Function} addToast - Add toast callback function
 * @description  Send bilk invites to users using emails or CSV file
 */
function InviteUsersSettingsPage({
  user,
  groups,
  community,
  dailyLimit = null,
  dailyLimitReset,
  addToast = (e) => {},
}) {
  const reducer = (state, newState) => ({ ...state, ...newState });
  const [switchEditor, setSwitchEditor] = useState(false);

  const initialState = {
    groupId: document.location.origin,
    additionalMessage: undefined,
    file: null,
    memberData: [{ email: "", name: "" }],
    sendingIndividualInvite: false,
    sendingCsvInvite: false,
    sendingTestMail: false,
    coverPictureUploadPercent: 0,
    savingMailTemplate: false,
  };

  const [
    {
      groupId,
      additionalMessage,
      file,
      memberData,
      sendingIndividualInvite,
      sendingCsvInvite,
      sendingTestMail,
      coverPictureUploadPercent,
      savingMailTemplate,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  const communityId = community?.id;

  // Get mail template
  useEffect(() => {
    EmailService.getMailTemplate(user, communityId)
      .then(({ configurable }) => {
        dispatch({
          additionalMessage: configurable.configurableValue,
        });
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {});
  }, [user, communityId]);

  // Send individual invite to user
  /**
   * @param {Array<Object>} list - list of users to invite
   */
  function handleIndividualInvite(list) {
    const validEmails = list.filter((member) => {
      return member.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
    }).length;
    if (validEmails !== list.length) {
      // sendInvite();
      addToast("Please enter a valid email address", "", ToastTypes.danger);
    }
    if (
      !window.confirm(
        `Are you sure you want to send invite mail to ${list.length} ${
          list.length === 1 ? "user" : "users"
        } ?`
      )
    ) {
      return;
    }
    //  // Fake Invites
    // const emailList = Array.from({ length: 10 }, (index) => {
    //   return {
    //     email: `test${Math.floor(Math.random() * 100)}@test.com`,
    //     name: `test${Math.floor(Math.random() * 100)}`,
    //   };
    // });
    // console.log(emailList);
    const payload = {
      groupId: groupId == document.location.origin ? null : groupId,
      additionalMessage: additionalMessage,
      memberData: list,
    };
    dispatch({ sendingIndividualInvite: true });
    EmailService.inviteByEmail(user, community.id, payload)
      .then(() => {
        addToast("Invitation sent successfully", "", ToastTypes.success);
        dispatch({ memberData: [{ email: "", name: "" }] });
      })
      .catch((err) => {
        addToast("Error sending invitation", "", ToastTypes.danger);
        console.log(err);
      })
      .finally(() => {
        dispatch({ sendingIndividualInvite: false });
      });
  }

  // Send invites from csv file
  function handleCsvInvite(e) {
    if (!file || file === null) {
      addToast("Please select a file", "", ToastTypes.danger);
    } else {
      const payload = {
        groupId: groupId == document.location.origin ? "" : groupId,
        additionalMessage: additionalMessage,
      };
      if (!window.confirm("Are you sure you want to send invite mail ?")) {
        return;
      }
      dispatch({ sendingCsvInvite: true });
      EmailService.inviteByCSV(
        user,
        community.id,
        payload,
        file,
        (percentComplete) => {
          dispatch({ coverPictureUploadPercent: percentComplete });
        }
      )
        .then(() => {
          addToast("Invitation sent successfully", "", ToastTypes.success);
          dispatch({ file: null });
        })
        .catch((err) => {
          addToast("Error sending invitation", "", ToastTypes.danger);
          console.log(err);
        })
        .finally(() => {
          dispatch({ sendingCsvInvite: false, coverPictureUploadPercent: 0 });
        });
    }
  }

  // Send test mail
  function handleTestMail() {
    if (!user.email) {
      addToast("Please link your email to your account", "", ToastTypes.info);
      return;
    }
    const payload = {
      groupId: groupId == document.location.origin ? null : groupId,
      additionalMessage: additionalMessage,
      memberData: [
        {
          email: user.email,
          name: user.name,
        },
      ],
    };
    dispatch({ sendingTestMail: true });
    EmailService.inviteByEmail(user, community.id, payload)
      .then(() => {
        addToast("Invitation sent successfully", "", ToastTypes.success);
        dispatch({ file: null });
      })
      .catch((err) => {
        addToast("Error sending invitation", "", ToastTypes.danger);
        console.log(err);
      })
      .finally(() => {
        dispatch({ sendingTestMail: false });
      });
  }

  // Save mail template
  function handleSaveTemplate(mailBody) {
    dispatch({ savingMailTemplate: true });
    EmailService.saveMailTemplate(user, community.id, mailBody)
      .then(() => {
        addToast("Template saved successfully", "", ToastTypes.success);
      })
      .catch((err) => {
        addToast("Error saving template", "", ToastTypes.danger);
        console.log(err);
      })
      .finally(() => {
        dispatch({ savingMailTemplate: false });
      });
  }

  return (
    <div className="Card rounded shadow theme-bg-default border theme-border-default mx-4 sm:mx-0">
      <Header dailyLimit={dailyLimit} dailyLimitReset={dailyLimitReset} />
      {/* <Body /> */}
      <div className="p-4 flex-none flex flex-col lg:flex-row justify-between overflow-hidden md:space-x-4 ">
        <div className="w-full lg:w-1/2 overflow-x-hidden ">
          <div className="flex items-center space-x-2 theme-text-subtitle-1">
            <span className="flex-none">Invite your members to </span>

            {
              <select
                className="flex-grow focus:outline-none theme-text-primary theme-bg-default max-w-sm"
                value={groupId}
                onChange={(e) => {
                  dispatch({ groupId: e.target.value });
                }}>
                <option value="">{document.location.origin}</option>
                {Array.isArray(groups) &&
                  groups.map((group, index) => {
                    return (
                      <option key={index} value={group.id}>
                        {group.name && group.name.length > 40
                          ? `${group.name.substring(0, 40)} ..`
                          : group.name}
                      </option>
                    );
                  })}
              </select>
            }
          </div>
          {/* Invite via link */}
          <InviteViaLink />
          {/* Invite Members Individually */}
          <InviteIndividual />
          {/* Upload CSV */}
          <InviteFromCSV />
        </div>
        {/* Mail template */}

        <div className="lg:w-1/2 mt-4 md:mt-0">
          <div className="flex justify-between items-center">
            <div className="text-sm font-semibold">Mail Template</div>
            <TextButton
              className="flex items-center"
              onClick={() => {
                if (!sendingTestMail) handleTestMail();
              }}
              prefix={sendingTestMail ? <Loader size={15} stroke={1} /> : null}
              label={sendingTestMail ? "Sending.." : "Send Test Mail"}
              isLoading={sendingTestMail}
            />
          </div>
          {/* {Template} */}
          <DisplayTemplate />
          <div className="w-full mt-4 md:mt-0">
            {/* Sample template */}
            <div className="flex items-center justify-between border theme-border-default rounded-lg theme-bg-default p-3 w-full flex-grow focus:outline-none resize-none mb-1 mt-4">
              Switch to rich text editor
              <InputSwitch
                selected={switchEditor}
                className="mx-2 transform duration-500 ease-in-out"
                onClick={(e) => {
                  setSwitchEditor(!switchEditor);
                }}
                alt
              />
            </div>
            <div className="mt-4 ">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold ">Add custom message</div>
                {/* Save Mail Template */}
                <TextButton
                  className="flex items-center"
                  onClick={() => {
                    if (!savingMailTemplate)
                      handleSaveTemplate(additionalMessage);
                  }}
                  prefix={
                    savingMailTemplate ? <Loader size={15} stroke={1} /> : null
                  }
                  label={savingMailTemplate ? "Saving.." : "Save"}
                  isLoading={savingMailTemplate}
                  disabled={
                    !additionalMessage || additionalMessage.length === 0
                  }
                />
              </div>
              <div>
                <div className="flex flex-col space-y-2">
                  {switchEditor ? (
                    <div className="flex flex-col space-y-1">
                      <Suspense fallback={<></>}>
                        <RichTextEditor
                          className=" rounded theme-bg-surface outline-none"
                          setText={(e) => {
                            dispatch({ additionalMessage: e });
                          }}
                          text={additionalMessage}
                        />
                      </Suspense>
                      <span className="theme-text-subtitle-2 text-xs">
                        Double click or select text to access formatting
                        options.
                      </span>
                    </div>
                  ) : (
                    <textarea
                      type="text"
                      value={additionalMessage}
                      onChange={(e) => {
                        dispatch({ additionalMessage: e.target.value });
                        window.setTimeout(() => {
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }, 0);
                      }}
                      className="px-2 py-3 w-full flex-grow block border theme-border-default theme-bg-surface focus:outline-none"
                      rows="9"
                      placeholder={"Enter message here"}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <EditorBasics />
        </div>
      </div>
    </div>
  );

  function Header({ dailyLimit, dailyLimitReset }) {
    return (
      <>
        <div className="flex flex-col pl-4 pr-2 pt-4 pb-4 shadow-sm w-full theme-bg-surface">
          <div className="w-full flex items-center justify-between">
            <div className="flex items-center justify-between w-full">
              <div className="flex-none font-bold theme-text-heading-2 text-base">
                Invite Members
              </div>
              {dailyLimit ? (
                <div>
                  {dailyLimit} invite emails remaining today. Resets at{" "}
                  {dailyLimitReset}.
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Invites by link section
  function InviteViaLink() {
    const inviteLink = window.location.origin;
    return (
      <div className="pl-4 pr-4 pt-4 pb-4 mt-2 rounded border theme-border-default theme-bg-surface">
        <div className="flex items-center mb-2">
          <i className="fi fi-rr-link text-sm"></i>
          <div className="font-semibold pl-2">Invite via link</div>
        </div>
        <CopyTextField text={inviteLink} className="theme-bg-default" />
      </div>
    );
  }

  // Invites by email section
  function InviteIndividual() {
    const [userData, setUserData] = useState(memberData);
    return (
      <div className="pl-4 pr-4 pt-4 pb-4 theme-bg-surface mt-2 rounded border theme-border-default">
        <div className="font-semibold">Invite via email</div>
        <div className="">
          {userData.map((user, index) => (
            <div
              key={index}
              className="flex justify-between items-start mt-2 space-x-1">
              <TextInput
                placeholder="Name"
                noPrefixSeparator
                noMargin
                large
                defaultValue={user.name}
                inputHolderClassName="theme-bg-default"
                containerClassName="focus:outline-none theme-bg-surface rounded"
                onChange={(e) => {
                  const newUserList = [...userData];
                  newUserList[index].name = e;
                  setUserData(newUserList);
                }}
              />
              <TextInput
                placeholder="Email Id"
                type="email"
                noPrefixSeparator
                validator={(value) => {
                  if (!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*$/.test(value)) {
                    return "Email is not valid!";
                  }
                }}
                noMargin
                large
                defaultValue={user.email}
                inputHolderClassName="theme-bg-default"
                containerClassName="focus:outline-none theme-bg-surface rounded ml-2 md:ml-0 flex-grow"
                onChange={(e) => {
                  const newUserList = [...userData];
                  newUserList[index].email = e;
                  setUserData(newUserList);
                }}
              />

              <IconButton2
                icon="cross"
                size="sm"
                hoverable
                onClick={() => {
                  const newUserList = [...userData];
                  newUserList.splice(index, 1);

                  setUserData(newUserList);
                }}
              />
            </div>
          ))}
        </div>
        {/* <div className="theme-text-primary font-semibold mt-2 cursor-pointer">
          + add more
        </div> */}
        <TextButton
          prefix={<UIcon icon={"plus-small"} />}
          className="flex items-center mt-1"
          label="Add more"
          onClick={() => {
            setUserData([...userData, { name: "", email: "" }]);
          }}
        />
        <div className="mt-2">
          <Button
            disabled={
              !userData.length ||
              !userData.every((user) =>
                /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*$/.test(user.email)
              )
            }
            onClick={(e) => {
              if (userData.length > 0) {
                dispatch({ memberData: userData });
                handleIndividualInvite(userData);
              }
            }}
            className="p-4 w-full"
            label="Send Invite"
            large
            type="submit"
            isLoading={sendingIndividualInvite}
          />
        </div>
      </div>
    );
  }

  // Invites by csv section
  function InviteFromCSV() {
    const fileRef = useRef();

    const onFileSelect = async (e) => {
      let regex = new RegExp("(.*?).(csv)$");
      if (
        e.target.files[0] &&
        !regex.test(e.target.files[0].name.toLowerCase())
      ) {
        alert("Please select CSV file format");
      } else {
        let selectedFile = e.target.files[0];
        dispatch({
          file: selectedFile,
        });
      }
    };

    return (
      <div className="pl-4 pr-4 pt-4 pb-4 theme-bg-surface mt-2 rounded border theme-border-default">
        <div className="flex items-center mb-2">
          <div className="font-semibold">Upload a CSV</div>
        </div>
        <div className="theme-text-subtitle-1">
          Upload a CSV file with header as name and email
        </div>
        <div className="flex items-center theme-text-subtitle-2 mt-2">
          <span>Here is a</span>
          {/* <div className="theme-text-primary pl-1">template CSV file</div> */}
          <TextButton
            label="template CSV file"
            onClick={() => {
              window.open(
                `${socketEndpointBase}/sample-csvs/sample-community-user-invite.csv`
              );
            }}
            hoverable={false}
          />
        </div>
        <div
          className="w-full relative rounded cursor-pointer overflow-hidden theme-bg-default mt-3"
          onClick={(e) => {
            fileRef.current.click();
          }}>
          {/* file control */}
          <input
            className="hidden"
            accept=".csv, .xls, .xlsm, .xlsx"
            type="file"
            ref={fileRef}
            onChange={(e) => {
              onFileSelect(e);
            }}
          />
          <div className="flex place-content-center items-center border border-dotted pt-2 pb-2 theme-border-default select-none h-10">
            {!file ? (
              <i className="fi fi-rr-clip text-sm theme-text-subtitle-2"></i>
            ) : null}
            <div className="flex justify-center pl-2 mb-1 theme-text-subtitle-2">
              {file ? (
                <span className="flex items-center space-x-1 theme-text-primary">
                  {file.name}
                  <IconButton2
                    icon="cross"
                    size="xs"
                    iconClass="theme-text-primary"
                    hoverable
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dispatch({
                        file: null,
                      });
                    }}
                  />
                </span>
              ) : (
                "Upload File"
              )}
            </div>
          </div>
          <UploadProgressBar percentComplete={coverPictureUploadPercent} />
        </div>
        <div className="mt-4">
          <Button
            disabled={!file}
            onClick={handleCsvInvite}
            className="p-4 w-full"
            label="Send Invite"
            large
            isLoading={sendingCsvInvite}
          />
        </div>
      </div>
    );
  }

  // MAil template
  function DisplayTemplate() {
    return (
      <div className="pl-1 pr-1 pt-4 pb-4 theme-bg-surface mt-2 rounded border theme-border-default">
        <div className="font-semibold text-xs pl-2">{" Hey {name},"}</div>
        <div className="font-semibold text-xs mt-8 pl-2">
          {"{invited_by_name} has invited you to join the {community_name}"}
        </div>
        <div className="mt-8 pl-2">
          <Button
            disabled={true}
            className="p-4 pr-10 pl-10"
            label="Join"
            large
          />
        </div>
      </div>
    );
  }
  // Mail body editor info
  function EditorBasics() {
    return (
      <div>
        <div className="font-semibold mt-2 ">Editor Basics</div>
        <ul className="">
          <li className="theme-text-subtitle-2 text-xs pt-0.5">
            {"Use {name} for Name of user"}
          </li>
          <li className="theme-text-subtitle-2 text-xs pt-0.5">
            {"Use {email} for email of user"}
          </li>
          <li className="theme-text-subtitle-2 text-xs pt-0.5">
            {"Use {invited_by_name} for invited by user name"}
          </li>
          <li className="theme-text-subtitle-2 text-xs pt-0.5">
            {"Use {group_name} for group name"}
          </li>
          <li className="theme-text-subtitle-2 text-xs pt-0.5">
            {"Use {community_name} for community name"}
          </li>
          <li className="theme-text-subtitle-2 text-xs pt-0.5">
            {"Use {community_url} for community address"}
          </li>
          <li className="theme-text-subtitle-2 text-xs pt-0.5">
            {"Do not use any space between {}"}
          </li>
        </ul>
      </div>
    );
  }
}

const InviteMembersSettingsPage = withToast(
  connect((s) => ({
    user: s.auth,
    community: s.community,
    groups: s.groups,
  }))(InviteUsersSettingsPage)
);

export default InviteMembersSettingsPage;
